import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { DeleteSubmissionControl, type RemovedProject } from '@/components/event-interest';
import {
  Badge,
  Button,
  EmptyState,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  ValuePills,
  namePills,
} from '@/components/ui';
import { EmailLink } from '@/components/ui/TextLink';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useAuth } from '@/lib/use-auth';
import type {
  EventInterestCityChoice,
  EventInterestEventAnswer,
  EventInterestFormRegion,
  EventInterestSubmission,
  Paginated,
} from '@/lib/types';

/*
 * What this institution said on the public event-interest form, on its own
 * record.
 *
 * 🔴 IT DELETES A SUBMISSION NOW, FOR AN ADMINISTRATOR, ON THE OWNER'S
 * DECISION. ⚠️ SUPERSEDED: "READ-ONLY, and structurally so: nothing here
 * creates, edits, deletes or re-sends anything, and no control below writes ...
 * this panel offers no path to it, and a later pass must not add one by
 * symmetry with the other panels on this page, which do write."
 *
 * The control confirms through a dialog naming every project of interest the
 * delete takes the company off. Taking a project of interest off closes no
 * sales pursuit (owner's decision 2026-10-12).
 *
 * ⛔ AND THERE IS STILL NO UN-SUBMIT, NO EDIT AND NO RE-SEND. A submission is
 * the record of what a person told us: deciding it should not have been
 * recorded is a judgement somebody may take, and REWRITING what they said is
 * not. A later pass must not add either by symmetry with the delete.
 *
 * ⛔ AND THE PANEL STILL WRITES NOTHING ITSELF. The request, the permission and
 * the wording all live in `DeleteSubmissionControl`, which the tracker's own
 * dialog mounts too — so the two screens cannot come to word one destruction
 * differently, and neither can offer it to a reader the other refuses.
 *
 * ## `embedded`
 *
 * By default (`embedded` absent or `false`) this renders its own `Panel` with a
 * `PanelHeader`, exactly as it always has — every existing call site is
 * unchanged. With `embedded: true` it renders NEITHER: the body is returned
 * directly, for a host (a collapsible section on the company page) that has
 * already drawn the border and the heading, so the panel is not a card inside a
 * card with two headings. Every state, sentence, gate and callback is the same
 * in both modes; only the outer chrome goes. `HEADING_ID` is rendered only with
 * the chrome, so nothing points at it when embedded.
 */

const HEADING_ID = 'company-event-interest-heading';

/**
 * The three states this surface must always have, as one union so that no
 * render can be in two of them at once.
 *
 * ⚠️ `error` and `ready` are SEPARATE ARMS rather than a message sitting beside
 * a row list, because zero submissions is the ordinary answer here: a failed
 * read that still drew the empty state would tell a reader this institution
 * said nothing, which is a claim rather than a gap.
 */
type SubmissionsState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; response: Paginated<EventInterestSubmission> };

/*
 * 🔴 THE SINGLE MOST DANGEROUS CELL IN THIS COMPONENT, AND THE ONE A CAREFUL
 * READER GETS WRONG.
 *
 * An `all_cities` answer stores NO city rows at all — that is deliberate in the
 * schema, because materialising "all" goes stale the moment a stop is added to
 * or dropped from a tour. So the array is empty and the natural rendering,
 * "no cities chosen", is the EXACT OPPOSITE of what the institution said: they
 * asked for the WHOLE TOUR.
 *
 * ⛔ And do NOT "fix" the empty array by listing the project's stops instead.
 * "All" is the answer they gave; a list is a snapshot of a tour that changes,
 * and it would go on claiming stops that were dropped and silently omit stops
 * that were added — a confident wrong answer where this sentence is a correct
 * one that never rots.
 *
 * ⚠️ THIS SENTENCE IS NOT HELPER TEXT AND A SWEEP MUST NOT DELETE IT. It is a
 * FACT ABOUT THE DATA that the reader cannot otherwise see — the row renders no
 * cities and the reader has no way to tell "the whole tour" from "none" — which
 * is one of the kinds of sentence the no-helper-text prohibition keeps. It is
 * attached to a VALUE, not to a control, and it explains nothing.
 */
const ALL_CITIES_LABEL = 'All cities on this tour';

/*
 * 🔴 KEYED ON THE UNION, NOT ON `string`, AND THAT IS THE WHOLE POINT OF THIS
 * DECLARATION. A `Record<string, string>` WIDENS THE KEY, so a stale spelling
 * typechecks, matches nothing, and falls through to the verbatim branch below —
 * which renders `north-america form` where *North America form* belongs, on
 * every submission from that form. No error, no blank, nothing to grep for.
 *
 * That is not hypothetical: this map shipped with `north_america` (underscore)
 * and was wrong on every North America row until the key type caught it. Keyed
 * on {@link EventInterestFormRegion} the same mistake is a COMPILE ERROR, and
 * it catches the next one too.
 *
 * ⛔ `north-america` IS LOWERCASE WITH A HYPHEN — it is `admin-app`'s wire
 * format, already stored in their tables, and it can never move. ⚠️ DO NOT
 * "NORMALISE" IT: `BillingRegion` in this same repo carries `north_america`
 * with an UNDERSCORE, also meaning North America, and that one is a MySQL ENUM
 * member whose INDEX is stored — so it can never move either. Two lookalikes
 * one character apart, both correct, neither able to change, and NO type spans
 * the boundary. A find-and-replace on either silently breaks the other.
 */
const FORM_REGION_LABELS: Record<EventInterestFormRegion, string> = {
  'north-america': 'North America',
  global: 'Global',
};

/*
 * Same keying, same reason — it is not wrong today and it was unprotected by
 * the same mechanism, which is exactly the state the region map was in.
 *
 * ⚠️ Keyed off the FIELD rather than a named type because there is no
 * `EventInterestInclusion` to import: the union is declared inline on the
 * answer. Deriving it here keeps one source of truth — widening the field
 * breaks this line — where re-typing the members by hand would be a second copy
 * free to drift.
 */
const INCLUSION_LABELS: Record<NonNullable<EventInterestEventAnswer['inclusion']>, string> = {
  travel_package: 'Travel package',
  participation_only: 'Participation only',
};

/*
 * ⚠️ THE REGION IS NOT DECORATION AND IS THE ONLY THING THAT SEPARATES TWO
 * FACTS. A festival with no row on a submission was either never put in front
 * of this institution, or was — and the form they were sent is what says which.
 * Without this badge, "they said no to MENA" and "MENA was never on their form"
 * are the same empty space. Keep it.
 */
function formRegionLabel(region: EventInterestFormRegion): string {
  /*
   * ⚠️ THE FALLBACK NO LONGER GUARDS A TYPO — the key type does that now, and
   * it is the better guard because it fails at build time. What is left is the
   * one case a type cannot catch: this bundle meeting a SERVER AHEAD OF IT.
   * The region is a `VARCHAR` with its values enforced in zod rather than a SQL
   * `ENUM`, so a third form is a schema edit on their side and can arrive
   * without anything here changing — and the union is a claim about the wire,
   * not a guarantee about it.
   *
   * So it is dead to the compiler and live at runtime, which is why it stays:
   * a raw value is odd-looking and TRUE, where the blank it replaces is a
   * region silently erased.
   */
  return `${FORM_REGION_LABELS[region] ?? region} form`;
}

/*
 * ⛔ NEVER `eventKey` OR `cityKey`, HERE OR ANYWHERE. Both travel with the
 * submission as an AUDIT of the exact strings the public form sent, kept so
 * that what a customer submitted is never rewritten — and both are joined on by
 * nothing. `project.id` and `city.id` are the identity. This codebase has been
 * bitten four times by matching on a second, admin-editable vocabulary.
 *
 * ⚠️ Neither embed is nullable and neither gets a fallback branch: an event key
 * that resolves to no EMERGE project is refused at submission time with a 422,
 * so an "unknown festival" state cannot reach this renderer. A `?? 'Unknown'`
 * here would be a branch that can never fire, which reads to the next reader as
 * a state the data can hold.
 */
function cityNameOf(choice: EventInterestCityChoice): string {
  return choice.name;
}

/*
 * The cities line for one festival. Three arms, three different meanings, and
 * `selection` is the ONLY thing that separates them:
 *
 *   all_cities     → the whole tour. Carries no `cities` key at all. See above.
 *   select_cities  → the stops they named, in the order the server sent them.
 *   null           → a single-location festival, or a decline. They were asked
 *                    NEITHER question, so there is nothing to say — and "no
 *                    cities" here would invent a refusal they never made.
 *
 * ⚠️ THE TYPE IS A DISCRIMINATED UNION AND THAT IS LOAD-BEARING, NOT STYLE.
 * `answer.cities` does not compile until `selection` has been narrowed, because
 * an `all_cities` answer and a single-location festival BOTH arrive as an empty
 * list on the wire — so `cities.length === 0` reads as "they chose nothing" on
 * the one answer that means they chose everything. ⛔ Do not widen this back to
 * an optional array, and do not reach for `?? []` or `?.length`: either re-arms
 * exactly that bug.
 */
function citiesLineOf(answer: EventInterestEventAnswer): ReactNode {
  if (answer.selection === 'all_cities') return ALL_CITIES_LABEL;
  if (answer.selection !== 'select_cities') return null;
  /*
   * The server refuses `select_cities` with no cities — it says "these specific
   * ones" and names none — so an empty array here is a contract defect rather
   * than a state. Rendering nothing is the least-wrong response to it: the
   * alternatives are a bare separator, or a sentence asserting something about
   * cities on a row whose city list is the thing that went missing.
   */
  const names = answer.cities.map(cityNameOf);
  return names.length > 0 ? <ValuePills items={namePills(names)} /> : null;
}

/**
 * The loading placeholder, in the real shape rather than a spinner — one
 * submission block with its header line, its contact line and three festival
 * rows. ONE block, not several: almost every account has nought or one, and a
 * stack of placeholders would promise a history that is not there.
 */
function SubmissionsSkeleton() {
  return (
    <div className="px-4 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <Skeleton className="h-3.5 w-40" />
        <Skeleton className="h-4 w-24" />
      </div>
      <Skeleton className="mt-2 h-3 w-52" />
      <div className="mt-3 grid gap-3">
        {Array.from({ length: 3 }, (_, row) => (
          <div key={row} className="grid gap-1.5">
            <Skeleton className="h-3.5 w-48" />
            <Skeleton className="h-3 w-32" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CalendarIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="h-5 w-5"
    >
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4" />
      <path d="M16 3v4" />
      <path d="M3 11h18" />
    </svg>
  );
}

function FestivalAnswer({ answer }: { answer: EventInterestEventAnswer }) {
  const said = answer.response === 'yes';
  const cities = citiesLineOf(answer);

  return (
    <li className="grid gap-1 border-t border-border pt-2 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="min-w-0 text-sm text-fg [overflow-wrap:anywhere]">
          {answer.projectName}
        </span>
        {/*
          ⚠️ A DECLINE HAS A ROW OF ITS OWN AND IS RENDERED. Dropping the "no"
          answers would leave a reader unable to tell a festival this
          institution turned down from one that was never on their form — and
          only the first of those is a sales fact. Tone is never the only
          signal: both badges carry their word.
        */}
        <Badge tone={said ? 'success' : 'neutral'}>{said ? 'Yes' : 'No'}</Badge>
        {said && answer.inclusion !== null && (
          <Badge tone="neutral" variant="outline">
            {INCLUSION_LABELS[answer.inclusion] ?? answer.inclusion}
          </Badge>
        )}
      </div>
      {said && cities !== null && (
        <div className="m-0 text-xs text-fg-secondary [overflow-wrap:anywhere]">{cities}</div>
      )}
    </li>
  );
}

/**
 * The projects this submission's `yes` answers named — what deleting it takes
 * the company off, and what the confirmation states one by one.
 *
 * ⚠️ **`yes` ONLY.** A `no` is a real answer with a row of its own and it named
 * no interest, so listing it would claim the delete removes something it does
 * not.
 *
 * ⚠️ **`projectId` IS THE IDENTITY** — never `eventKey`, which travels as an
 * audit of the exact string the public form sent and is joined on by nothing.
 */
function removedProjectsOf(submission: EventInterestSubmission): RemovedProject[] {
  return submission.events
    .filter((answer) => answer.response === 'yes')
    .map((answer) => ({ key: String(answer.projectId), name: answer.projectName }));
}

function SubmissionBlock({
  submission,
  onDeleted,
}: {
  submission: EventInterestSubmission;
  onDeleted: () => void;
}) {
  /*
   * ⚠️ `pursuitsCreatedAt: null` MEANS NOT YET. IT NEVER MEANS FAILED, and the
   * two must not share a word or a tone.
   *
   * A submission attaches to its company immediately, while that company may
   * still be awaiting approval — and a pending company deliberately has nothing
   * created for it until an approver approves it. So `null` is the CORRECT and
   * ordinary state of a brand-new institution, not a problem anybody should act
   * on. Both badges are therefore `neutral`: a warning tone here would put an
   * amber flag on every account in the approval queue, which is exactly the
   * alarm that gets muted within a week.
   *
   * The word carrying the meaning is "yet" — it says not done, never went
   * wrong.
   */
  const pursuitsAt = submission.pursuitsCreatedAt;

  /*
   * 🔴 THREE STATES, NOT TWO, AND ONLY TWO VALUES TO CARRY THEM. A rejected
   * company's submission survives and points at it, and the follow-up is never
   * run for one — so `null` there is PERMANENT, where on a pending company it
   * means the approver has not got to it. Reading `null` as "yet" on both is a
   * promise that one of them will never keep, and `pursuitsCreatedAt` alone
   * cannot tell them apart. `company.approvalStatus` is what does.
   *
   * ⚠️ The rejected wording drops "yet" and adds NOTHING else: it must not read
   * as a failure either, because nothing failed — a decision was taken.
   */
  const pursuitsLabel =
    pursuitsAt !== null
      ? 'Projects of interest added'
      : submission.company.approvalStatus === 'rejected'
        ? 'Projects of interest not added'
        : 'Projects of interest not added yet';

  /*
   * 🔴 TWO FIELDS, THREE STATES, AND ONE OF THEM IS STILL AMBIGUOUS — which is
   * why that one renders NOTHING rather than a third confident answer.
   *
   *   dealId set,  dealCreatedAt set  → a deal exists
   *   dealId null, dealCreatedAt set  → it was raised and has since been DELETED
   *   both null                       → not attempted, OR it failed
   *
   * ⚠️ THIS PANEL DELIBERATELY DREW NOTHING ABOUT THE DEAL UNTIL `dealCreatedAt`
   * LANDED, and that was right at the time: `dealId` is `ON DELETE SET NULL`, so
   * on its own it carried *no deal was raised* and *the deal was destroyed* on
   * one value — two opposite facts, and a cell picking either was wrong on half
   * the rows it drew. The second stamp is what separates them.
   *
   * The ambiguity did not vanish, it SHRANK: the bottom row is still two facts
   * on one value. So the rule is applied to what is left of it rather than
   * abandoned because most of it went — the first two states are said, the third
   * is not drawn at all.
   *
   * ⛔ AND `dealId === null` IS NOT A LICENCE TO OFFER "raise the deal". On the
   * middle row that raises a SECOND deal for a submission the contract allows
   * exactly ONE of. This panel offers no such control and must not gain one;
   * anything that ever does has to read BOTH fields first.
   *
   * ⚠️ Neutral tone on both. A deal is working state somebody may legitimately
   * delete — the foreign key is `SET NULL` rather than `RESTRICT` precisely so
   * they can — so "since deleted" reports a fact, never a fault.
   */
  /*
   * 🔴 **`typeof === 'string'`, NEVER `!== null` — this shipped the badge it
   * exists to prevent.** The key was absent from the wire, so it read
   * `undefined`, `undefined !== null` is **true**, and EVERY submission with no
   * deal wore a **"Deal raised, since deleted"** badge titled `Invalid Date` —
   * about a deal that was never raised.
   *
   * ⚠️ **THE FIELD IS ON THE WIRE NOW AND THE GUARD IS UNCHANGED.** *SUPERSEDED
   * 2026-09-18, the justification and not the test: "`dealCreatedAt` has never
   * been on the wire (measured 2026-09-18: the backend emits `dealId` and
   * `pursuitsCreatedAt` and no third field)" … "Until the server sends the
   * field, a deal somebody really did delete carries no badge rather than the
   * wrong one."* Migration `225_add_event_interest_deal_created_at.sql` shipped
   * the column and the mapper emits `dealCreatedAt` — so the middle row is
   * answerable in production and the badge is now a REPORT rather than an
   * under-report.
   *
   * ⛔ **THAT IS A REASON TO KEEP `typeof === 'string'`, NOT TO RELAX IT.** The
   * key is still OPTIONAL on the type, because this app deploys separately from
   * the API and can be NEWER than it — and on such a server the field is absent
   * again, `!== null` is true again, and the wrong badge is back on every row.
   * The test answers *is there a timestamp here*, which is the question; `!==
   * null` answers *is this key not literally null*, which is a different one
   * that happened to agree once the field existed.
   *
   * ⚠️ **The same defect was live on `SubmissionDetailModal` and was found
   * there first, in a browser; this second site was found by the COMPILER**,
   * the moment `dealCreatedAt` was made optional in `types.ts`. That is the
   * argument for widening the type rather than patching the branch: the type
   * names every consumer, and prose names none.
   */
  const dealLabel =
    submission.dealId !== null
      ? 'Deal raised'
      : typeof submission.dealCreatedAt === 'string'
        ? 'Deal raised, since deleted'
        : null;

  return (
    <article className="border-b border-border px-4 py-4 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <h3 className="m-0 text-sm font-semibold text-fg">
          {formatDateTime(submission.submittedAt)}
        </h3>
        <Badge tone="neutral" variant="outline">
          {formRegionLabel(submission.formRegion)}
        </Badge>
        <Badge
          tone="neutral"
          variant="outline"
          {...(pursuitsAt !== null ? { title: formatDateTime(pursuitsAt) } : {})}
        >
          {pursuitsLabel}
        </Badge>
        {dealLabel !== null && (
          <Badge
            tone="neutral"
            variant="outline"
            {...(typeof submission.dealCreatedAt === 'string'
              ? { title: formatDateTime(submission.dealCreatedAt) }
              : {})}
          >
            {dealLabel}
          </Badge>
        )}
      </div>

      {/*
        Who filled the form in. Name and email are both required by the form, so
        neither is a blank to hedge; the designation is optional and is simply
        absent when nobody gave one, rather than rendering a dash that reads
        like a value.
      */}
      <p className="m-0 mt-1.5 text-xs text-fg-secondary [overflow-wrap:anywhere]">
        {submission.contactName}
        {submission.contactDesignation ? ` · ${submission.contactDesignation}` : ''}
        {' · '}
        <EmailLink address={submission.contactEmail} tone="muted" />
        {submission.contactPhone ? ` · ${submission.contactPhone}` : ''}
      </p>

      {/*
        ⚠️ RENDERED BECAUSE IT IS THE ONLY STRING TYING THIS ROW TO A ROW IN THE
        SYSTEM THAT COLLECTED IT. `admin-app` records every submission locally
        even when this API is unreachable and replays it later, so this is the
        first thing anybody asks for when the two disagree — and there is
        nothing else on screen that could answer it.
      */}
      <p className="m-0 mt-0.5 text-2xs text-fg-muted [overflow-wrap:anywhere]">
        Reference {submission.sourceReference}
      </p>

      {/*
        Rendered in the order the server sent them. The festivals are NOT
        re-sorted into yes-then-no: a reader comparing two submissions is
        reading the same form twice, and reordering the second by its answers
        would make the two lists line up differently.
      */}
      {submission.events.length > 0 && (
        <ul className="m-0 mt-3 grid list-none gap-2 p-0">
          {/*
            Keyed on the PROJECT, which is the natural key — one answer per
            festival per submission, `UNIQUE` on the server. The row carries no
            surrogate id of its own, deliberately: a field declared here and not
            served would read back `undefined`, which is how a React key
            silently becomes nothing.
          */}
          {submission.events.map((answer) => (
            <FestivalAnswer key={answer.projectId} answer={answer} />
          ))}
        </ul>
      )}

      {/*
        ⚠️ **BELOW THE ANSWERS, NOT BESIDE THE HEADING.** The dialog names the
        projects of interest it will remove, and this is the one place a reader
        can already see those answers — so the control sits after them rather
        than in the badge row, where it would be the first thing pressed on a
        record nobody had read yet.

        ⚠️ **The control renders NOTHING for a reader who may not delete**, so
        this row collapses to an empty flex box for everybody else. It carries
        no sentence of its own: a why-you-cannot-do-this line on every
        submission block of every company record is the helper text the house
        rule forbids, and there is no control here for it to explain.
      */}
      <div className="mt-3 flex justify-end">
        <DeleteSubmissionControl
          submissionId={submission.id}
          institution={submission.company.name}
          submittedAt={submission.submittedAt}
          projects={removedProjectsOf(submission)}
          onDeleted={onDeleted}
          size="sm"
        />
      </div>
    </article>
  );
}

/**
 * ⚠️ Deliberately NOT given the deal. `deal_id` is `ON DELETE SET NULL`, so a
 * `null` there is EITHER "no deal was raised" OR "the deal was raised and has
 * since been deleted" — two different facts wearing one value, and nothing on
 * the wire separates them. A cell that picked either reading would be a
 * confident wrong answer on half the rows it drew, so nothing is drawn. Read
 * `pursuitsCreatedAt` for whether the submission was processed; that is the
 * field written to answer it.
 */
export function CompanyEventInterestPanel({
  companyId,
  onDeleted,
  embedded = false,
}: {
  companyId: number;
  /** See the header: `true` drops the outer `Panel` and `PanelHeader`. Default `false`. */
  embedded?: boolean;
  /**
   * 🔴 **CALLED WHEN A SUBMISSION IS DELETED, AND THE PAGE MUST RE-READ THE
   * COMPANY ON IT.**
   *
   * Deleting a submission takes the company off every project of interest its
   * `yes` answers named — and that list is rendered by a DIFFERENT field on
   * this same page, from `company.projectsOfInterest`. This panel re-reads
   * itself, so without this the reader watches the submission disappear while
   * the Projects of interest row a few inches above it goes on stating the old
   * set, with nothing failing.
   *
   * ⚠️ **It means RE-READ, never CLOSE.** `CompanyDetailPage.refreshRecord`
   * bumps a nonce and re-fetches; several callbacks named like this elsewhere
   * in this app mean *close the dialog*, and wiring one of those in here would
   * unmount the panel that was about to show the shorter list.
   *
   * ⚠️ **Optional, so a surface with nothing else to refresh may mount this
   * panel without one** — the delete still works and the panel still re-reads
   * its own list.
   */
  onDeleted?: () => void;
}) {
  const { ability } = useAuth();
  const canRead = ability.canRead('event-interest');

  const [nonce, setNonce] = useState(0);
  const [result, setResult] = useState<{ key: string; state: SubmissionsState } | null>(null);

  const key = `${companyId}:${nonce}`;

  /**
   * ⚠️ **TWO REFRESHES, AND BOTH ARE NEEDED.** The nonce re-reads this panel's
   * own list so the deleted block goes; `onDeleted` is what tells the page to
   * re-read the COMPANY, whose projects of interest the same delete changed.
   * Doing only the first leaves a stale list one field away on the same screen.
   *
   * ⚠️ **It reuses the retry's nonce deliberately** — a second counter would be
   * a second way to say "read this again", and the two would drift the first
   * time somebody changed what a refresh means here.
   */
  const handleDeleted = useCallback(() => {
    setNonce((value) => value + 1);
    onDeleted?.();
  }, [onDeleted]);

  useEffect(() => {
    // The effect bails rather than the component returning early, because hooks
    // run unconditionally — and a reader who may not see this panel must not
    // spend a request on it either.
    if (!canRead) return;

    const controller = new AbortController();
    /*
     * Newest first, asked of the SERVER rather than sorted here — a browser
     * sort would order the page that happened to arrive and present it as the
     * whole answer.
     *
     * ⚠️ NO `perPage`, deliberately: an institution has a handful of these at
     * most, so the server default is ample and guessing a ceiling would fetch a
     * hundred rows of seven festivals each onto a record page. The rare account
     * that outruns one page is covered by the footer below, which says so
     * rather than letting the reader count what is on screen and believe it.
     */
    api
      .listEventInterestSubmissions(
        { companyId, sort: 'submittedAt', order: 'desc' },
        controller.signal,
      )
      .then((response) => {
        if (controller.signal.aborted) return;
        setResult({ key, state: { status: 'ready', response } });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setResult({
          key,
          state: {
            status: 'error',
            message:
              caught instanceof Error
                ? caught.message
                : 'The event interest submissions couldn’t be loaded.',
          },
        });
      });
    /*
     * ⚠️ NO `.finally`, AND ITS ABSENCE IS A DECISION RATHER THAN AN OMISSION.
     * The house rule is that a fetch guards `signal.aborted` in both `.catch`
     * and `.finally`, and the reason it names `.finally` is the `setLoading(false)`
     * that normally lives there. There is none here: loading is DERIVED from
     * the key below — exactly as `CompanyReportsField` on this same page does
     * it — so a settled flag would be redundant with the two writes above.
     *
     * An empty `.finally` guarded on `aborted` would therefore be a guard that
     * can never fire, which this codebase treats as worse than no guard at all:
     * it reads to the next person as a live rule. The rule's actual purpose —
     * never write state after an abort — is honoured at both sites that write.
     */
    return () => controller.abort();
  }, [canRead, companyId, key]);

  /*
   * A reader without the module gets NOTHING — no panel, no refusal block.
   *
   * The refusal-panel rule this app follows elsewhere is about a whole PAGE
   * somebody reached by URL, where rendering nothing is indistinguishable from
   * the app being broken. This is one panel among several on a record page that
   * renders completely without it, so there is no such ambiguity: a refusal
   * here would be a permanent grey box on every company record, telling a
   * reader about a module they were never given, on a page that is otherwise
   * entirely fine for them.
   *
   * ⚠️ `event-interest` is a REAL module gated on permission sets, so below the
   * admin tier an absent scope resolves ALLOWED for `read` — which means this
   * panel shows to everybody until the permissions migration lands, and the
   * server's own refusal is what it meets. That fails into the error block
   * below, truthfully, rather than into a blank.
   */
  if (!canRead) return null;

  /*
   * Derived during render rather than set at the top of the effect: there is no
   * window in which a retry looks settled, and a change of company cannot leave
   * the previous account's submissions on screen reading as this one's.
   */
  const state: SubmissionsState = result?.key === key ? result.state : { status: 'loading' };
  const submissions = state.status === 'ready' ? state.response.data : [];
  const total = state.status === 'ready' ? state.response.meta.total : 0;
  const error = state.status === 'error' ? state.message : null;

  const content = (
    <>
      {/*
        ⚠️ THE THREE STATES ARE MUTUALLY EXCLUSIVE, AND THE ERROR ARM RENDERS
        INSTEAD OF THE EMPTY ONE RATHER THAN ABOVE IT. A read that failed and an
        institution that submitted nothing are different facts, and zero
        submissions is the ORDINARY answer here — so an "Excellent, none" empty
        state under a red banner is the one arrangement a reader could take for
        a settled result. The retry below writes nothing; it re-reads.
      */}
      <div aria-busy={state.status === 'loading' || undefined}>
        {state.status === 'loading' ? (
          <SubmissionsSkeleton />
        ) : error !== null ? (
          <div
            role="alert"
            className="flex flex-col items-start gap-2 px-4 py-4 text-xs text-danger"
          >
            <span className="font-semibold">Couldn’t load event interest.</span>
            <span className="min-w-0 [overflow-wrap:anywhere]">{error}</span>
            <Button size="sm" onClick={() => setNonce((value) => value + 1)}>
              Try again
            </Button>
          </div>
        ) : submissions.length === 0 ? (
          /*
            ⛔ NO DESCRIPTION, AND ITS ABSENCE IS THE DECISION. The house rule is
            that an empty state says what to do next — and here there is nothing
            to do: this panel is read-only and a submission arrives from a public
            form nobody on this screen can fill in. Every sentence that would fit
            ("submissions will appear here") promises data is on its way, which
            is the one thing empty-state copy may never do.

            ⚠️ AND ZERO IS THE ORDINARY ANSWER, NOT AN EDGE. Almost every account
            has none, so this is a primary screen rather than a fallback. It must
            read as a settled fact — which is why it is not toned, not iconless
            by accident, and carries no hedge about the read having worked; a
            read that failed is the block above, not this.
          */
          <EmptyState icon={<CalendarIcon />} title="No event interest submissions" />
        ) : (
          <div>
            {submissions.map((submission) => (
              <SubmissionBlock
                key={submission.id}
                submission={submission}
                onDeleted={handleDeleted}
              />
            ))}
          </div>
        )}
      </div>

      {/*
        A fact about the DATA the reader cannot otherwise see, and therefore one
        of the sentences the no-helper-text rule keeps: the list is a page, and
        without this a reader counts what is on screen and believes it is the
        whole answer. It renders only when the server says there is more.
      */}
      {total > submissions.length && (
        <PanelFooter>
          <p className="m-0 text-xs text-fg-muted">
            Showing the first {submissions.length} of {total} submissions.
          </p>
        </PanelFooter>
      )}
    </>
  );

  if (embedded) return content;

  return (
    <Panel aria-labelledby={HEADING_ID}>
      <PanelHeader id={HEADING_ID} title="Event interest" />
      {content}
    </Panel>
  );
}
